import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CreateAuctionTab - Multi-step auction creation wizard
 * Step 1: Search and select diamond
 * Step 2: Configure auction parameters
 * Step 3: Review and create
 */
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { FaSearch, FaGem, FaCog, FaCheckCircle, FaArrowRight, FaArrowLeft, FaGavel, FaExclamationTriangle, FaTimesCircle, FaLock, FaGlobe, } from 'react-icons/fa';
import { apiClient } from '../../../utils/apiClient';
import { useAuctionAdmin } from '../../../hooks/useAuctionAdmin';
// ── Styled Components ────────────────────────────────────────
const Container = styled.div `
  padding: 0;
  max-width: 900px;
`;
const StepIndicator = styled.div `
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 2rem;
  padding: 0 1rem;
`;
const StepItem = styled.div `
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  position: relative;
`;
const StepCircle = styled.div `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.3s;
  background: ${props => props.$completed ? '#10b981'
    : props.$active ? 'var(--accent-gold)'
        : 'var(--bg-tertiary)'};
  color: ${props => props.$completed || props.$active ? 'white' : 'var(--text-tertiary)'};
  border: 2px solid ${props => props.$completed ? '#10b981'
    : props.$active ? 'var(--accent-gold)'
        : 'var(--border-color)'};
`;
const StepLabel = styled.div `
  font-size: 0.85rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: ${props => props.$active ? 'var(--text-primary)' : 'var(--text-tertiary)'};
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;
const StepConnector = styled.div `
  flex: 1;
  height: 2px;
  background: ${props => props.$completed ? '#10b981' : 'var(--border-color)'};
  margin: 0 0.5rem;
  min-width: 20px;
`;
const StepContent = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;
const StepTitle = styled.h3 `
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const StepDescription = styled.p `
  margin: 0 0 1.5rem 0;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;
const SearchContainer = styled.div `
  position: relative;
  margin-bottom: 1.5rem;
`;
const SearchInput = styled.input `
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.95rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: all 0.2s;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }
`;
const SearchIcon = styled.div `
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
`;
const DiamondList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
`;
const DiamondCard = styled.div `
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid ${props => props.$selected ? 'var(--accent-gold)' : 'var(--border-color)'};
  border-radius: 10px;
  background: ${props => props.$selected ? 'var(--accent-gold-bg, rgba(230, 192, 104, 0.08))' : 'var(--bg-tertiary)'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold);
    transform: translateX(4px);
  }
`;
const DiamondIcon = styled.div `
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(59, 130, 246, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #8b5cf6;
  flex-shrink: 0;
`;
const DiamondInfo = styled.div `
  flex: 1;
  min-width: 0;

  h4 {
    margin: 0 0 0.25rem 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    color: var(--text-secondary);
  }
`;
const DiamondPrice = styled.div `
  font-size: 1rem;
  font-weight: 700;
  color: var(--accent-gold);
  font-family: 'JetBrains Mono', monospace;
  white-space: nowrap;
`;
const FormGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const FormGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
`;
const FormLabel = styled.label `
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const FormInput = styled.input `
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  box-sizing: border-box;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const FormSelect = styled.select `
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  box-sizing: border-box;

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }
`;
const FormHint = styled.span `
  font-size: 0.75rem;
  color: var(--text-tertiary);
  font-weight: 400;
`;
const ReviewSection = styled.div `
  margin-bottom: 1.5rem;
`;
const ReviewTitle = styled.h4 `
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const ReviewGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const ReviewItem = styled.div `
  padding: 0.75rem 1rem;
  background: var(--bg-tertiary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;
const ReviewLabel = styled.div `
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  margin-bottom: 0.25rem;
`;
const ReviewValue = styled.div `
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
`;
const ButtonRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
`;
const NavButton = styled.button `
  padding: 0.75rem 1.75rem;
  border-radius: 8px;
  border: 1px solid ${props => props.$primary ? 'var(--accent-gold)' : 'var(--border-color)'};
  background: ${props => props.$primary ? 'var(--accent-gold)' : 'var(--bg-tertiary)'};
  color: ${props => props.$primary ? 'white' : 'var(--text-primary)'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background: ${props => props.$primary ? 'var(--accent-gold-dark)' : 'var(--bg-secondary)'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
const Alert = styled.div `
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  background: ${props => props.$type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'};
  color: ${props => props.$type === 'error' ? '#ef4444' : '#10b981'};
  border: 1px solid ${props => props.$type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'};
`;
const SearchingState = styled.div `
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;
const EmptySearch = styled.div `
  text-align: center;
  padding: 2rem;
  color: var(--text-tertiary);
  font-size: 0.9rem;
`;
// ── Component ────────────────────────────────────────────────
const CreateAuctionTab = () => {
    const { createAuction, isCreating, createError } = useAuctionAdmin();
    // Step management
    const [currentStep, setCurrentStep] = useState(1);
    // Step 1: Diamond selection
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedDiamond, setSelectedDiamond] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    // Step 2: Auction configuration
    const [auctionType, setAuctionType] = useState('automated');
    const [visibility, setVisibility] = useState('public');
    const [minimumDealerTier, setMinimumDealerTier] = useState(1);
    const [startingPrice, setStartingPrice] = useState('');
    const [reservePrice, setReservePrice] = useState('');
    const [minimumIncrementPercent, setMinimumIncrementPercent] = useState('5');
    const [scheduledStartTime, setScheduledStartTime] = useState('');
    const [scheduledEndTime, setScheduledEndTime] = useState('');
    const [gracePeriodHours, setGracePeriodHours] = useState('2');
    const [maxGraceExtensions, setMaxGraceExtensions] = useState('3');
    // Step 3: Result
    const [createdAuctionId, setCreatedAuctionId] = useState(null);
    // ── Search Diamonds ──────────────────────────────────────
    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim())
            return;
        setSearching(true);
        setHasSearched(true);
        try {
            const response = await apiClient.get(`diamonds-inventory?search=${encodeURIComponent(searchQuery)}&limit=20`);
            if (response.ok && response.data?.success && response.data.data?.diamonds) {
                setSearchResults(response.data.data.diamonds);
            }
            else {
                setSearchResults([]);
            }
        }
        catch {
            setSearchResults([]);
        }
        finally {
            setSearching(false);
        }
    }, [searchQuery]);
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };
    // ── Validation ───────────────────────────────────────────
    const isStep2Valid = () => {
        return !!(startingPrice &&
            Number(startingPrice) > 0 &&
            minimumIncrementPercent &&
            Number(minimumIncrementPercent) > 0 &&
            scheduledStartTime &&
            scheduledEndTime &&
            new Date(scheduledEndTime) > new Date(scheduledStartTime));
    };
    // ── Create Auction ───────────────────────────────────────
    const handleCreate = async () => {
        if (!selectedDiamond)
            return;
        const input = {
            diamondId: selectedDiamond.id,
            diamondKey1: selectedDiamond.shape || selectedDiamond.id,
            auctionType,
            visibility,
            minimumDealerTier,
            startingPrice: Number(startingPrice),
            minimumIncrementPercent: Number(minimumIncrementPercent),
            reservePrice: reservePrice ? Number(reservePrice) : undefined,
            scheduledStartTime: new Date(scheduledStartTime).toISOString(),
            scheduledEndTime: new Date(scheduledEndTime).toISOString(),
            gracePeriodHours: Number(gracePeriodHours),
            maxGraceExtensions: Number(maxGraceExtensions),
        };
        const result = await createAuction(input);
        if (result.success && 'auctionId' in result && result.auctionId) {
            setCreatedAuctionId(result.auctionId);
            setCurrentStep(3);
        }
    };
    // ── Reset form ───────────────────────────────────────────
    const handleReset = () => {
        setCurrentStep(1);
        setSearchQuery('');
        setSearchResults([]);
        setSelectedDiamond(null);
        setHasSearched(false);
        setAuctionType('automated');
        setVisibility('public');
        setMinimumDealerTier(1);
        setStartingPrice('');
        setReservePrice('');
        setMinimumIncrementPercent('5');
        setScheduledStartTime('');
        setScheduledEndTime('');
        setGracePeriodHours('2');
        setMaxGraceExtensions('3');
        setCreatedAuctionId(null);
    };
    const steps = [
        { label: 'Select Diamond', icon: _jsx(FaGem, {}) },
        { label: 'Configure', icon: _jsx(FaCog, {}) },
        { label: 'Review & Create', icon: _jsx(FaCheckCircle, {}) },
    ];
    return (_jsxs(Container, { children: [_jsx(StepIndicator, { children: steps.map((step, index) => (_jsxs(React.Fragment, { children: [_jsxs(StepItem, { "$active": currentStep === index + 1, "$completed": currentStep > index + 1, children: [_jsx(StepCircle, { "$active": currentStep === index + 1, "$completed": currentStep > index + 1, children: currentStep > index + 1 ? _jsx(FaCheckCircle, {}) : index + 1 }), _jsx(StepLabel, { "$active": currentStep === index + 1, children: step.label })] }), index < steps.length - 1 && (_jsx(StepConnector, { "$completed": currentStep > index + 1 }))] }, index))) }), createError && (_jsxs(Alert, { "$type": "error", children: [_jsx(FaTimesCircle, {}), " ", createError] })), currentStep === 1 && (_jsxs(StepContent, { children: [_jsxs(StepTitle, { children: [_jsx(FaGem, {}), " Select Diamond"] }), _jsx(StepDescription, { children: "Search your inventory to find the diamond you want to auction." }), _jsxs(SearchContainer, { children: [_jsx(SearchIcon, { children: _jsx(FaSearch, {}) }), _jsx(SearchInput, { placeholder: "Search by stock number, shape, carat, certificate...", value: searchQuery, onChange: e => setSearchQuery(e.target.value), onKeyDown: handleSearchKeyDown })] }), _jsx("div", { style: { marginBottom: '1rem' }, children: _jsxs(NavButton, { onClick: handleSearch, disabled: !searchQuery.trim() || searching, children: [_jsx(FaSearch, {}), " ", searching ? 'Searching...' : 'Search Diamonds'] }) }), searching ? (_jsx(SearchingState, { children: "Searching inventory..." })) : searchResults.length > 0 ? (_jsx(DiamondList, { children: searchResults.map(diamond => (_jsxs(DiamondCard, { "$selected": selectedDiamond?.id === diamond.id, onClick: () => setSelectedDiamond(diamond), children: [_jsx(DiamondIcon, { children: _jsx(FaGem, {}) }), _jsxs(DiamondInfo, { children: [_jsxs("h4", { children: [diamond.caratWeight, "ct ", diamond.shape, " ", diamond.color, " ", diamond.clarity] }), _jsxs("p", { children: [diamond.stockNumber || 'No stock #', " \u2022", ' ', diamond.lab, " ", diamond.certificateNumber || 'No cert', " \u2022", ' ', "$", diamond.pricePerCarat?.toLocaleString(), "/ct"] })] }), _jsxs(DiamondPrice, { children: ["$", diamond.totalPrice?.toLocaleString()] })] }, diamond.id))) })) : hasSearched ? (_jsxs(EmptySearch, { children: [_jsx(FaExclamationTriangle, { style: { marginRight: '0.5rem' } }), "No diamonds found. Try a different search term."] })) : (_jsx(EmptySearch, { children: "Enter a search query and press Enter or click Search." })), _jsxs(ButtonRow, { children: [_jsx("div", {}), _jsxs(NavButton, { "$primary": true, disabled: !selectedDiamond, onClick: () => {
                                    if (selectedDiamond) {
                                        // Pre-fill starting price from diamond price
                                        if (!startingPrice) {
                                            setStartingPrice(String(Math.round(selectedDiamond.totalPrice * 0.7)));
                                        }
                                        setCurrentStep(2);
                                    }
                                }, children: ["Continue ", _jsx(FaArrowRight, {})] })] })] })), currentStep === 2 && (_jsxs(StepContent, { children: [_jsxs(StepTitle, { children: [_jsx(FaCog, {}), " Configure Auction"] }), _jsxs(StepDescription, { children: ["Set auction parameters for", ' ', _jsxs("strong", { children: [selectedDiamond?.caratWeight, "ct ", selectedDiamond?.shape, ' ', selectedDiamond?.color, " ", selectedDiamond?.clarity] })] }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsxs(FormLabel, { children: [_jsx(FaGavel, {}), " Auction Type"] }), _jsxs(FormSelect, { value: auctionType, onChange: e => setAuctionType(e.target.value), children: [_jsx("option", { value: "automated", children: "Automated (Timed)" }), _jsx("option", { value: "supervised", children: "Supervised (Live)" })] }), _jsx(FormHint, { children: auctionType === 'automated'
                                            ? 'Runs automatically with countdown timer'
                                            : 'Admin controls bidding flow in real-time' })] }), _jsxs(FormGroup, { children: [_jsxs(FormLabel, { children: [visibility === 'public' ? _jsx(FaGlobe, {}) : _jsx(FaLock, {}), " Visibility"] }), _jsxs(FormSelect, { value: visibility, onChange: e => setVisibility(e.target.value), children: [_jsx("option", { value: "public", children: "Public - All Dealers" }), _jsx("option", { value: "private", children: "Private - Invited Only" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Minimum Dealer Tier" }), _jsxs(FormSelect, { value: minimumDealerTier, onChange: e => setMinimumDealerTier(Number(e.target.value)), children: [_jsx("option", { value: 1, children: "Tier 1 - All Dealers" }), _jsx("option", { value: 2, children: "Tier 2 - Preferred+" }), _jsx("option", { value: 3, children: "Tier 3 - Premium Only" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Minimum Bid Increment (%)" }), _jsx(FormInput, { type: "number", min: "1", max: "50", step: "0.5", value: minimumIncrementPercent, onChange: e => setMinimumIncrementPercent(e.target.value), placeholder: "5" }), _jsx(FormHint, { children: "Percentage increase required per bid" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Starting Price ($)" }), _jsx(FormInput, { type: "number", min: "1", value: startingPrice, onChange: e => setStartingPrice(e.target.value), placeholder: "Enter starting price" }), _jsxs(FormHint, { children: ["Diamond listed at $", selectedDiamond?.totalPrice?.toLocaleString()] })] }), _jsxs(FormGroup, { children: [_jsxs(FormLabel, { children: ["Reserve Price ($) ", _jsx(FormHint, { children: "Optional" })] }), _jsx(FormInput, { type: "number", min: "0", value: reservePrice, onChange: e => setReservePrice(e.target.value), placeholder: "No reserve" }), _jsx(FormHint, { children: "Minimum price to sell (hidden from bidders)" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Scheduled Start" }), _jsx(FormInput, { type: "datetime-local", value: scheduledStartTime, onChange: e => setScheduledStartTime(e.target.value) })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Scheduled End" }), _jsx(FormInput, { type: "datetime-local", value: scheduledEndTime, onChange: e => setScheduledEndTime(e.target.value), min: scheduledStartTime || undefined })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Grace Period (hours)" }), _jsx(FormInput, { type: "number", min: "0", max: "48", value: gracePeriodHours, onChange: e => setGracePeriodHours(e.target.value) }), _jsx(FormHint, { children: "Extra time added after last-second bids" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Max Grace Extensions" }), _jsx(FormInput, { type: "number", min: "0", max: "10", value: maxGraceExtensions, onChange: e => setMaxGraceExtensions(e.target.value) }), _jsx(FormHint, { children: "Maximum number of grace period extensions" })] })] }), _jsxs(ButtonRow, { children: [_jsxs(NavButton, { onClick: () => setCurrentStep(1), children: [_jsx(FaArrowLeft, {}), " Back"] }), _jsxs(NavButton, { "$primary": true, disabled: !isStep2Valid(), onClick: () => setCurrentStep(3), children: ["Review ", _jsx(FaArrowRight, {})] })] })] })), currentStep === 3 && (_jsx(StepContent, { children: createdAuctionId ? (
                // Success state
                _jsxs(_Fragment, { children: [_jsxs(Alert, { "$type": "success", children: [_jsx(FaCheckCircle, {}), " Auction created successfully!"] }), _jsxs("div", { style: { textAlign: 'center', padding: '2rem 0' }, children: [_jsx(FaCheckCircle, { style: { fontSize: '4rem', color: '#10b981', marginBottom: '1rem' } }), _jsx("h3", { style: { margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }, children: "Auction Created" }), _jsxs("p", { style: { margin: '0 0 0.5rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }, children: ["Auction ID: ", _jsx("code", { style: { color: 'var(--accent-gold)' }, children: createdAuctionId })] }), _jsxs("p", { style: { margin: 0, color: 'var(--text-tertiary)', fontSize: '0.85rem' }, children: [selectedDiamond?.caratWeight, "ct ", selectedDiamond?.shape, ' ', selectedDiamond?.color, " ", selectedDiamond?.clarity, " \u2022", ' ', auctionType, " \u2022 Starts ", new Date(scheduledStartTime).toLocaleString()] })] }), _jsxs(ButtonRow, { children: [_jsx("div", {}), _jsx(NavButton, { "$primary": true, onClick: handleReset, children: "Create Another Auction" })] })] })) : (
                // Review state
                _jsxs(_Fragment, { children: [_jsxs(StepTitle, { children: [_jsx(FaCheckCircle, {}), " Review & Create"] }), _jsx(StepDescription, { children: "Verify auction details before creating." }), _jsxs(ReviewSection, { children: [_jsx(ReviewTitle, { children: "Diamond" }), _jsxs(DiamondCard, { "$selected": false, style: { cursor: 'default' }, children: [_jsx(DiamondIcon, { children: _jsx(FaGem, {}) }), _jsxs(DiamondInfo, { children: [_jsxs("h4", { children: [selectedDiamond?.caratWeight, "ct ", selectedDiamond?.shape, ' ', selectedDiamond?.color, " ", selectedDiamond?.clarity] }), _jsxs("p", { children: [selectedDiamond?.stockNumber || 'No stock #', " \u2022", ' ', selectedDiamond?.lab, " ", selectedDiamond?.certificateNumber || 'No cert'] })] }), _jsxs(DiamondPrice, { children: ["$", selectedDiamond?.totalPrice?.toLocaleString()] })] })] }), _jsxs(ReviewSection, { children: [_jsx(ReviewTitle, { children: "Auction Configuration" }), _jsxs(ReviewGrid, { children: [_jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Type" }), _jsx(ReviewValue, { style: { textTransform: 'capitalize' }, children: auctionType })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Visibility" }), _jsx(ReviewValue, { style: { textTransform: 'capitalize' }, children: visibility })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Starting Price" }), _jsxs(ReviewValue, { children: ["$", Number(startingPrice).toLocaleString()] })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Reserve Price" }), _jsx(ReviewValue, { children: reservePrice ? `$${Number(reservePrice).toLocaleString()}` : 'No Reserve' })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Min Increment" }), _jsxs(ReviewValue, { children: [minimumIncrementPercent, "%"] })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Dealer Tier" }), _jsxs(ReviewValue, { children: ["Tier ", minimumDealerTier, "+"] })] })] })] }), _jsxs(ReviewSection, { children: [_jsx(ReviewTitle, { children: "Schedule" }), _jsxs(ReviewGrid, { children: [_jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Start" }), _jsx(ReviewValue, { children: new Date(scheduledStartTime).toLocaleString() })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "End" }), _jsx(ReviewValue, { children: new Date(scheduledEndTime).toLocaleString() })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Grace Period" }), _jsxs(ReviewValue, { children: [gracePeriodHours, "h"] })] }), _jsxs(ReviewItem, { children: [_jsx(ReviewLabel, { children: "Max Extensions" }), _jsx(ReviewValue, { children: maxGraceExtensions })] })] })] }), _jsxs(ButtonRow, { children: [_jsxs(NavButton, { onClick: () => setCurrentStep(2), children: [_jsx(FaArrowLeft, {}), " Edit Configuration"] }), _jsxs(NavButton, { "$primary": true, onClick: handleCreate, disabled: isCreating, children: [_jsx(FaGavel, {}), " ", isCreating ? 'Creating...' : 'Create Auction'] })] })] })) }))] }));
};
export default CreateAuctionTab;
